import React from 'react';
import { REF_KIND_LABELS, type PrepCard, type PrepReference, type RefKind, type SourceKind, useContent } from './content';
import { Markdown } from './markdown';
import { PrepPack } from './PrepPack';
import { RubricView } from './Rubric';
import type { InterviewSetup } from './types';

export const TOPICS = ['positioning', 'strategy', 'Company', 'Role', 'Interviewers', 'Process', 'Questions', 'AI', 'Values', 'Market', 'CV probe', 'Summary'] as const;
const SOURCE_LABEL: Record<SourceKind, string> = { official: 'Official', public: 'Public', unverified: 'Unverified' };

const TOPIC_LABELS: Record<string, string> = { positioning: 'Your positioning', strategy: 'Strategy → TA' };
export const topicLabel = (topic: string): string => TOPIC_LABELS[topic.toLowerCase()] ?? topic;

/** The overview card that is pinned to the top of the library. */
const PINNED_PATTERNS = [/your story for this role/iu, /strategy shapes this ta role/iu];

export function isPinned(card: PrepCard): boolean {
  return PINNED_PATTERNS.some((pattern) => pattern.test(card.title));
}

function pinRank(card: PrepCard): number {
  const index = PINNED_PATTERNS.findIndex((pattern) => pattern.test(card.title));
  return index < 0 ? PINNED_PATTERNS.length : index;
}

type View = 'library' | 'quick' | 'references' | 'rubric' | 'script';

const REF_ORDER: ReadonlyArray<RefKind> = ['official', 'ideals_public', 'third_party', 'linkedin', 'other'];

function References({ query, references }: { query: string; references: ReadonlyArray<PrepReference> }) {
  const needle = query.trim().toLowerCase();
  const shown = references.filter((ref) => !needle || `${ref.title} ${ref.note} ${ref.url}`.toLowerCase().includes(needle));
  if (references.length === 0) return <p className="hint">No references are available yet.</p>;
  return (
    <div className="ref-groups">
      <p className="hint" role="status">{shown.length} of {references.length} references</p>
      {REF_ORDER.map((kind) => {
        const items = shown.filter((ref) => ref.kind === kind);
        if (items.length === 0) return null;
        return (
          <section key={kind}>
            <h3>{REF_KIND_LABELS[kind]}</h3>
            <ol className="ref-list">
              {items.map((ref) => (
                <li key={ref.id}>
                  {ref.url ? <a href={ref.url} rel="noopener noreferrer" target="_blank">{ref.title}</a> : <span>{ref.title}</span>}
                  {ref.note && <small>{ref.note}</small>}
                </li>
              ))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

export function matchesQuery(card: PrepCard, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [card.title, card.summary, card.tags.join(' '), card.markdown].some((part) => part.toLowerCase().includes(needle));
}

/** Rough reading time at 200 words per minute. */
export function readingMinutes(cards: ReadonlyArray<PrepCard>): number {
  const words = cards.reduce((sum, card) => sum + `${card.summary} ${card.markdown}`.split(/\s+/u).filter(Boolean).length, 0);
  return Math.max(1, Math.round(words / 200));
}

function SourceBadges({ card }: { card: PrepCard }) {
  if (card.sources.length === 0) return <span className={`source-badge source-badge--unverified`}>Unverified</span>;
  return (
    <span className="source-list">
      {card.sources.map((source, index) => (
        <span className="source-item" key={index}>
          <span className={`source-badge source-badge--${source.kind}`}>{SOURCE_LABEL[source.kind]}</span>
          {source.url ? (
            <a href={source.url} rel="noopener noreferrer" target="_blank">{source.label || source.url}</a>
          ) : (
            <span>{source.label}</span>
          )}
        </span>
      ))}
    </span>
  );
}

function CassieCard() {
  const profile = useContent()?.cassieProfile;
  if (!profile) return null;
  return (
    <details className="you-card">
      <summary>
        <span className="eyebrow">You</span>
        <strong>{profile.name}{profile.title ? ` — ${profile.title}` : ''}{profile.company ? `, ${profile.company}` : ''}</strong>
        {profile.linkedinUrl && (
          <a href={profile.linkedinUrl} onClick={(event) => event.stopPropagation()} rel="noopener noreferrer" target="_blank">LinkedIn</a>
        )}
      </summary>
      {profile.summary && <p>{profile.summary}</p>}
      {profile.strengths.length > 0 && (<><h4>Strengths</h4><ul>{profile.strengths.map((item, index) => <li key={index}>{item}</li>)}</ul></>)}
      {profile.gaps.length > 0 && (<><h4>Gaps to address</h4><ul>{profile.gaps.map((item, index) => <li key={index}>{item}</li>)}</ul></>)}
    </details>
  );
}

function Card({ card, defaultOpen = false }: { card: PrepCard; defaultOpen?: boolean }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <article className={`lib-card ${card.importance === 3 ? 'lib-card--must' : ''}`}>
      <button aria-expanded={open} className="lib-card-head" onClick={() => setOpen((value) => !value)} type="button">
        <span className="lib-card-meta">
          <span className="lib-topic">{topicLabel(card.topic)}</span>
          {card.importance === 3 && <span className="lib-must">Must know</span>}
        </span>
        <strong>{card.title}</strong>
        {card.summary && <span className="lib-summary">{card.summary}</span>}
      </button>
      <div className="lib-card-sources"><SourceBadges card={card} /></div>
      {open && (
        <div className="lib-card-body">
          {card.tags.length > 0 && <p className="lib-tags">{card.tags.map((tag) => `#${tag}`).join('  ')}</p>}
          <Markdown citations={card.sources} source={card.markdown || card.summary} />
        </div>
      )}
    </article>
  );
}

export function PrepLibrary({ setup }: { setup: InterviewSetup }) {
  const content = useContent();
  const library = content?.prepLibrary ?? [];
  const references = content?.references ?? [];
  const [view, setView] = React.useState<View>('library');
  const [topic, setTopic] = React.useState<string>('all');
  const [mustOnly, setMustOnly] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const topics = React.useMemo(() => {
    const present = new Set(library.map((card) => card.topic));
    const known = TOPICS.filter((item) => present.has(item));
    const extra = [...present].filter((item) => !(TOPICS as ReadonlyArray<string>).includes(item));
    return [...known, ...extra];
  }, [library]);

  const filtered = React.useMemo(
    () =>
      library.filter(
        (card) => (topic === 'all' || card.topic === topic) && (!mustOnly || card.importance === 3) && matchesQuery(card, query),
      ),
    [library, topic, mustOnly, query],
  );
  const quick = React.useMemo(
    () =>
      library
        .filter((card) => card.importance === 3)
        .sort((a, b) => topics.indexOf(a.topic) - topics.indexOf(b.topic)),
    [library, topics],
  );
  const pinned = filtered.filter(isPinned).sort((a, b) => pinRank(a) - pinRank(b));
  const rest = filtered.filter((card) => !isPinned(card));
  const printed = view === 'quick' ? quick : filtered;

  return (
    <main className="prep-page prep-page--embedded library-page">
      <div className="prep-heading">
        <p className="eyebrow">Interview prep</p>
        <h1>Know the company, the role and the room</h1>
        <CassieCard />
      </div>

      <div className="lib-views" role="tablist" aria-label="Prep views">
        {([
          ['library', 'Library'],
          ['quick', 'Must-know in 10 minutes'],
          ['rubric', 'How you’ll be scored'],
          ['references', 'References'],
          ['script', 'Call script & templates'],
        ] as const).map(([id, label]) => (
          <button aria-selected={view === id} className={view === id ? 'is-active' : ''} key={id} onClick={() => setView(id)} role="tab" type="button">
            {label}
          </button>
        ))}
      </div>

      {view === 'script' && <PrepPack backLabel="" embedded onBack={() => undefined} setup={setup} />}

      {view === 'rubric' && <RubricView />}

      {view === 'references' && (
        <>
          <div className="lib-controls">
            <input aria-label="Search references" onChange={(event) => setQuery(event.target.value)} placeholder="Search references…" type="search" value={query} />
          </div>
          <References query={query} references={references} />
        </>
      )}

      {(view === 'library' || view === 'quick') && library.length === 0 && (
        <p className="hint">The interview-prep library is not available yet. The call script and templates are under “Call script &amp; templates”.</p>
      )}

      {view === 'library' && library.length > 0 && (
        <>
          <div className="lib-controls">
            <input aria-label="Search the library" onChange={(event) => setQuery(event.target.value)} placeholder="Search title, tags and content…" type="search" value={query} />
            <label className="toggle-row">
              <input checked={mustOnly} onChange={(event) => setMustOnly(event.target.checked)} type="checkbox" />
              <span>Must know</span>
            </label>
            <button className="ghost-button lib-print" onClick={() => window.print()} type="button">Print / save as PDF</button>
          </div>
          <div className="lib-chips" role="group" aria-label="Topics">
            {['all', ...topics].map((item) => (
              <button aria-pressed={topic === item} className={topic === item ? 'is-active' : ''} key={item} onClick={() => setTopic(item)} type="button">
                {item === 'all' ? 'All' : topicLabel(item)}
              </button>
            ))}
          </div>
          <p className="hint" role="status">{filtered.length} of {library.length} cards</p>
          {pinned.length > 0 && (
            <div className="lib-pinned">
              <p className="eyebrow">Start here</p>
              {pinned.map((card) => <Card card={card} defaultOpen key={card.id} />)}
            </div>
          )}
          <div className="lib-grid">
            {rest.map((card) => <Card card={card} key={card.id} />)}
          </div>
          {filtered.length === 0 && <p className="hint">No cards match. Clear the search or filters.</p>}
        </>
      )}

      {view === 'quick' && library.length > 0 && (
        <>
          <p className="hint" role="status">
            {quick.length} must-know cards · about {readingMinutes(quick)} min to read
          </p>
          <button className="ghost-button lib-print" onClick={() => window.print()} type="button">Print / save as PDF</button>
          <ol className="lib-quick">
            {quick.map((card) => (
              <li key={card.id}><Card card={card} /></li>
            ))}
          </ol>
        </>
      )}

      {(view === 'library' || view === 'quick') && printed.length > 0 && (
        <section aria-hidden="true" className="print-only">
          <h1>Interview prep</h1>
          {printed.map((card) => (
            <article className="print-card" key={card.id}>
              <h2>{card.title}</h2>
              <p className="print-meta">{topicLabel(card.topic)}{card.importance === 3 ? ' · Must know' : ''}</p>
              <Markdown citations={card.sources} source={card.markdown || card.summary} />
              {card.sources.length > 0 && (
                <p className="print-meta">
                  Sources: {card.sources.map((source) => `${source.label || source.url} (${SOURCE_LABEL[source.kind]}${source.url ? `, ${source.url}` : ''})`).join('; ')}
                </p>
              )}
            </article>
          ))}
        </section>
      )}
    </main>
  );
}
